import { Box, Typography } from '@mui/material';
import FilterInput from '@libComponents/FilterInput';
import BasicMenu from 'common/components/tableHandlar/rowhandlar';
import { theme } from 'app/Theme';

/**
 * TableTopHeader – two‑column header bar.
 *
 * Left  : Page title
 * Right : Search input, optional extra action elements, column-visibility menu,
 *         and the primary action button (e.g. "Create Client").
 *
 * @param {Object}        props
 * @param {string}        props.title              - Page / section title.
 * @param {string}        [props.searchName]       - `name` prop forwarded to FilterInput.
 * @param {string}        [props.searchPlaceholder] - Placeholder forwarded to FilterInput.
 * @param {string}        props.searchValue        - Controlled search value.
 * @param {Function}      props.onSearch           - Called with the new search string.
 * @param {React.Node}    [props.primaryAction]    - Button / element placed after the search bar.
 * @param {React.Node}    [props.extraActions]     - Additional icon buttons placed between search and menu.
 * @param {Object}        [props.tableData]        - Column visibility map passed to BasicMenu.
 * @param {Function}      [props.setTableData]     - Setter for tableData (column visibility).
 * @param {boolean}       [props.showColumnMenu]   - Whether to show the column-visibility menu. Default true.
 */
const TableTopHeader = ({
    title,
    searchName = 'table-search',
    searchPlaceholder = 'Search',
    searchValue,
    onSearch,
    primaryAction,
    extraActions,
    tableData,
    setTableData,
    showColumnMenu = true
}) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            {/* Left: Title */}
            <Typography
                variant='h5'
                sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    '@media (max-width: 1024px)': { fontSize: '1.25rem' }
                }}
            >
                {title}
            </Typography>

            {/* Right: actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {onSearch && (
                    <Box
                        sx={{
                            width: 300,
                            background: theme.palette.common.paper,
                            '@media (max-width: 1024px)': { width: '100%' }
                        }}
                    >
                        <FilterInput
                            name={searchName}
                            placeholder={searchPlaceholder}
                            handleSearch={onSearch}
                            value={searchValue}
                        />
                    </Box>
                )}

                {extraActions}

                {showColumnMenu && tableData && setTableData && (
                    <BasicMenu tableData={tableData} setTableData={setTableData} />
                )}

                {primaryAction}
            </Box>
        </Box>
    );
};

export default TableTopHeader;
